/**
 * @fileoverview SessionConnectionIndicator Component
 * 
 * Displays a single WiFi icon representing the worst connection quality
 * between the viewer (proctor) and the student. Clicking the icon opens
 * a Popover with detailed connection info including per-track bitrate and FPS.
 * 
 * @module vtc/components/SessionCard/SessionConnectionIndicator
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Popover, IconButton, Icon, View, Text, Heading, Flex, Spinner, Tag } from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import { ConnectionQuality } from 'livekit-client';

/**
 * Quality ranking from worst (0) to best (4).
 * Used to determine which connection is the bottleneck.
 */
const QUALITY_RANK = {
  unknown: 0,
  [ConnectionQuality.Lost]: 1,
  [ConnectionQuality.Poor]: 2,
  [ConnectionQuality.Good]: 3,
  [ConnectionQuality.Excellent]: 4,
};

/**
 * Maps a quality value to an icon name, label, and color.
 *
 * @param {string} quality - LiveKit ConnectionQuality enum value
 * @param {string} connectionState - LiveKit ConnectionState value
 * @returns {{ iconName: string, label: string, color: string }}
 */
function getIconInfo(quality, connectionState) {
  if (connectionState === 'disconnected') {
    return { iconName: 'IconWifiOff', label: 'Disconnected', color: '#D01A19' };
  }

  if (connectionState === 'reconnecting') {
    return { iconName: 'IconWifi1', label: 'Reconnecting', color: '#C45500' };
  }

  switch (quality) {
    case ConnectionQuality.Excellent:
      return { iconName: 'IconWifi', label: 'Excellent', color: '#0B874B' };
    case ConnectionQuality.Good:
      return { iconName: 'IconWifi2', label: 'Good', color: '#0B874B' };
    case ConnectionQuality.Poor:
      return { iconName: 'IconWifi1', label: 'Poor', color: '#C45500' };
    case ConnectionQuality.Lost:
      return { iconName: 'IconWifiOff', label: 'Lost', color: '#D01A19' };
    default:
      return { iconName: 'IconQuestionLine', label: 'Unknown', color: '#0770A3' };
  }
}

/**
 * Returns the worse of two quality values.
 */
function getWorstQuality(viewerInfo, studentInfo) {
  const viewerRank = QUALITY_RANK[viewerInfo?.quality] ?? 0;
  const studentRank = QUALITY_RANK[studentInfo?.quality] ?? 0;

  // Pick whichever has the lower rank (worse quality)
  if (viewerRank <= studentRank) {
    return { quality: viewerInfo?.quality, state: viewerInfo?.state };
  }
  return { quality: studentInfo?.quality, state: studentInfo?.state };
}

/**
 * Formats a source name for display (e.g., 'camera' → 'Camera', 'screen_share' → 'Screen Share')
 */
function formatSource(source) {
  if (!source) return 'Unknown';
  return source
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Preferred display order for student track sources.
 * Lower number = higher in the list. Unlisted sources fall to the end.
 */
const SOURCE_ORDER = {
  screen_share: 0,
  camera: 1,
  microphone: 2,
};

/**
 * Formats bitrate to a human-readable string
 */
function formatBitrate(bitrate) {
  if (bitrate == null) return '—';
  if (bitrate >= 1000000) return `${Math.round(bitrate / 1000000)} Mbps`;
  if (bitrate >= 1000) return `${Math.round(bitrate / 1000)} kbps`;
  return `${Math.round(bitrate)} bps`;
}

/**
 * SessionConnectionIndicator Component
 * 
 * Displays a single WiFi IconButton representing the worst connection
 * quality between the viewer and student. Clicking opens a Popover
 * with detailed per-track stats fetched on demand.
 * 
 * @component
 * @param {Object} props
 * @param {string} props.sessionSid - Session identifier to get connection quality for
 */
function SessionConnectionIndicator({ sessionSid }) {
  const connectionInfo = useSessionStore(
    (state) => state.connectionQualities?.[sessionSid]
  );
  const getSessionViewerRef = useSessionStore((state) => state.getSessionViewerRef);

  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef(null);

  const worst = getWorstQuality(connectionInfo?.viewer, connectionInfo?.student);
  const { iconName, color } = getIconInfo(worst.quality, worst.state);

  const viewerIcon = getIconInfo(connectionInfo?.viewer?.quality, connectionInfo?.viewer?.state);
  const studentIcon = getIconInfo(connectionInfo?.student?.quality, connectionInfo?.student?.state);

  const fetchStats = useCallback(async () => {
    const ref = getSessionViewerRef(sessionSid);
    if (ref?.getConnectionStats) {
      const result = await ref.getConnectionStats();
      setStats(result);
    }
  }, [sessionSid, getSessionViewerRef]);

  // Poll stats every 2s while the popover is open (same as SessionDiagnostics)
  useEffect(() => {
    if (!isOpen) return;

    fetchStats().then(() => setIsLoading(false));

    intervalRef.current = setInterval(fetchStats, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, fetchStats]);

  const handleShow = useCallback(() => {
    setIsOpen(true);
    setIsLoading(true);
    setStats(null);
  }, []);

  const handleHide = useCallback(() => {
    setIsOpen(false);
    setStats(null);
  }, []);

  return (
    <Popover
      on="click"
      placement="bottom center"
      isShowingContent={isOpen}
      onShowContent={handleShow}
      onHideContent={handleHide}
      shouldReturnFocus
      renderTrigger={
        <IconButton
          screenReaderLabel="Connection quality"
          withBorder={false}
          withBackground={false}
          size="small"
          color="primary"
          themeOverride={{ smallHeight: '1.5rem' }}
          margin='none xxx-small none none'
        >
          <Icon name={iconName} size="x-small" style={{ color }} />
        </IconButton>
      }
    >
      <View as="div" padding="xx-small" width="280px">
        {/* Student Connection */}
        <View as="div" background="secondary" padding="xx-small" borderRadius="large" margin="0 0 x-small 0">
          <Flex alignItems="center" justifyItems="space-between">
            <Flex.Item shouldGrow>
              <Heading level="h4" as="h4">Test Taker Connection</Heading>
            </Flex.Item>
            <Flex.Item>
              <Tag text={studentIcon.label} size="small" themeOverride={{ defaultBackground: studentIcon.color, defaultColor: '#fff' }} />
            </Flex.Item>
          </Flex>
        </View>

        {isLoading && (
          <Flex justifyItems="center" padding="small">
            <Spinner size="x-small" renderTitle="Loading stats" />
          </Flex>
        )}

        {!isLoading && stats?.student?.tracks?.length > 0 && (
          <View as="div" margin="0 0 small small">
            {[...stats.student.tracks]
              .sort((a, b) => (SOURCE_ORDER[a.source] ?? 99) - (SOURCE_ORDER[b.source] ?? 99))
              .map((track) => (
                <Flex
                  key={track.trackSid}
                  justifyItems="space-between"
                  alignItems="center"
                  margin="0 0 xx-small 0"
                >
                  <Flex.Item shouldGrow shouldShrink>
                    <Text size="x-small" weight="bold">
                      {formatSource(track.source)}
                      {track.muted && (
                        <Text size="x-small" color="danger"> (Muted)</Text>
                      )}
                    </Text>
                  </Flex.Item>
                  <Flex.Item>
                    <Flex gap="xx-small" alignItems="center">
                      {/* 
                    {track.kind === 'video' && (
                      <View
                        as="span"
                        padding="0 x-small"
                        background="secondary"
                        borderRadius="pill"
                      >
                        <Text size="x-small">
                          {track.fps != null ? `${Math.round(track.fps)} fps` : '— fps'}
                        </Text>
                      </View>
                    )}
                    */}
                      <View
                        as="span"
                        padding="0 x-small"
                        background="secondary"
                        borderRadius="pill"
                      >
                        <Text size="x-small">{formatBitrate(track.bitrate)}</Text>
                      </View>

                    </Flex>
                  </Flex.Item>
                </Flex>
              ))}
          </View>
        )}

        {!isLoading && (!stats?.student?.tracks || stats.student.tracks.length === 0) && (
          <Text size="small" color="secondary">No tracks available</Text>
        )}

        {/* Viewer Connection */}
        <View as="div" background="secondary" padding="xx-small" borderRadius="large" margin="small 0 0 0">
          <Flex alignItems="center" justifyItems="space-between">
            <Flex.Item shouldGrow>
              <Heading level="h4" as="h4">Your Connection</Heading>
            </Flex.Item>
            <Flex.Item>
              <Tag text={viewerIcon.label} size="small" themeOverride={{ defaultBackground: viewerIcon.color, defaultColor: '#fff' }} />
            </Flex.Item>
          </Flex>
        </View>
      </View>
    </Popover>
  );
}

export default SessionConnectionIndicator;
